"use client"

import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { cva, type VariantProps } from "class-variance-authority"
import { Check, Minus } from "lucide-react"

import { cn } from "../utils"

const checkboxVariants = cva(
  "peer shrink-0 border border-primary shadow-sm " +
    "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring " +
    "disabled:cursor-not-allowed disabled:opacity-50 " +
    "data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground " +
    "data-[loading=true]:bg-muted data-[loading=true]:text-muted-foreground " +
    "data-[state=checked][data-loading=true]:bg-muted data-[state=checked][data-loading=true]:text-muted-foreground",
  {
    variants: {
      size: {
        default: "size-4 rounded-sm",
        lg: "size-5 rounded-md",
        xl: "size-6 rounded-lg",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
)

interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {
  loading?: boolean
}

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, size, loading = false, checked, ...props }, ref) => {
    const isVisuallyChecked = loading || checked === true || checked === "indeterminate"

    return (
      <CheckboxPrimitive.Root
        ref={ref}
        disabled={loading}
        checked={checked}
        data-loading={loading}
        data-state={isVisuallyChecked ? "checked" : "unchecked"}
        className={cn(checkboxVariants({ size }), className)}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          className={cn("flex items-center justify-center text-current")}
          {...(isVisuallyChecked && { forceMount: true })}
        >
          {checked === "indeterminate" ? (
            <Minus className={cn(size === "xl" ? "size-5" : "size-3.5")} />
          ) : (
            <Check className={cn(size === "xl" ? "size-5" : "size-4")} />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    )
  }
)
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox, checkboxVariants }
